import React, { useEffect, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import './DexInfo.css';
import AddressDisplay from '../addressdisplay/AddressDisplay';
import LockCircle from '../lockcircle/LockCircle';
import { MdOutlineInfo } from "react-icons/md";

const Info = MdOutlineInfo as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

interface PriceBoxProps {
  data: any;
  extraData: any;
  holderData: any;
  athData: any;
  scannerData: any;
  chainId: any;
}

const DexInfo: React.FC<PriceBoxProps> = ({ data, extraData, holderData, athData, scannerData, chainId }) => {
  const [scannerD, setScanner] = useState<any>(null);
  const [totalLocked, setTotalLocked] = useState<any>(null);

  useEffect(() => {
    if (!scannerData) return;
    const entries = Object.values(scannerData);
    if (!entries.length) return;
    const firstScanner = entries[0] as any;
    if (!firstScanner) return;

    setScanner(firstScanner);

    const holders = firstScanner.lp_holders;
    if (!holders || holders.length === 0) {
      // no holders → assume everything is locked
      setTotalLocked(100);
    } else {
      // start at 100%, subtract each unlocked holder’s percent*100
      const unlockedSum = holders.reduce((acc: number, holder: any) => {
        if (holder.is_locked === 0) {
          // percent is stored as a fraction string, e.g. "0.12"
          return acc + parseFloat(holder.percent) * 100;
        }
        return acc;
      }, 0);

      // clamp to [0,100]
      const totalLockedPercentage = Math.max(0, 100 - unlockedSum);
      setTotalLocked(totalLockedPercentage);
    }

  }, [scannerData]);

  const formatH = (h: number, price: number): string => {
    if (!price || price === 0) return '-';
    const multiplier = h / price;
    if (multiplier > 100000) {
      return '-';
    }
    return multiplier.toFixed(1) + 'x';
  };  

  const formatVolume = (volume: number): string => {
    if (volume === null || volume === undefined) return '-';

    if (Math.abs(volume) >= 1_000_000_000) {
      return (
        (volume / 1_000_000_000).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + 'B'
      );
    } else if (Math.abs(volume) >= 1_000_000) {
      return (
        (volume / 1_000_000).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + 'M'
      );
    } else if (Math.abs(volume) >= 1_000) {
      return (
        (volume / 1_000).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + 'K'
      );
    }
    return volume.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const relativeTime = (timestamp: any) => {
    // Convert seconds to milliseconds
    const ts = timestamp * 1000;
    const diffSeconds = Math.floor((Date.now() - ts) / 1000);
    if (diffSeconds < 60) return `${diffSeconds}s ago`;
    const diffMinutes = Math.floor(diffSeconds / 60);
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 365) return `${diffDays}d ago`;
    const diffYears = Math.floor(diffDays / 365);
    return `${diffYears}y ago`;
  };


  return (
    <>
      <div className='dex-info-wrapper'>
        <div className='dex-info'>
          <div 
            className="dex-title" 
            data-tooltip-id="global-tooltip"
            data-tooltip-content="Market Capitalization: The total market value of the token, calculated as circulating supply multiplied by the current price."
          >
            <Info /> MarketCap
          </div>
          <div className="dex-item">
            ${formatVolume(data.fdv)}
          </div>
        </div>
        <div className='dex-info'>
          <div 
            className="dex-title" 
            data-tooltip-id="global-tooltip"
            data-tooltip-content="Fully Diluted Valuation: The projected market cap if all possible tokens were in circulation."
          >
            <Info /> FDV
          </div>
          <div className="dex-item">
            ${formatVolume(data.fdv)}
          </div>
        </div>
      </div>
      <div className='dex-info-wrapper'>
        <div className='dex-info'>
          <div 
            className="dex-title" 
            data-tooltip-id="global-tooltip"
            data-tooltip-content="Volume: The total trading volume in the last 24 hours."
          >
            <Info /> Volume
          </div>
          <div className="dex-item">
            ${formatVolume(extraData?.["24h"]?.usd?.volume)}
          </div>
        </div>
        <div className='dex-info'>
          <div 
            className="dex-title" 
            data-tooltip-id="global-tooltip"
            data-tooltip-content="Liquidity: The amount of funds available in the liquidity pool, indicating the ease of trading the token. The lock icon shows the percentage locked."
          >
           <Info /> Liquidity
          </div>
          <div className="dex-item">
            
                ${formatVolume(data.liquidity)} {totalLocked && totalLocked !== 0 ? (<LockCircle lockedPercentage={totalLocked.toFixed(0)} />) : <LockCircle lockedPercentage={(0)} />}
          </div>
        </div>
      </div>
      <div className='dex-info-wrapper'>
        <div className='dex-info'>
          <div 
            className="dex-title" 
            data-tooltip-id="global-tooltip"
            data-tooltip-content="Current / ATH: Compares the current price multiplier to the All-time High multiplier. A higher multiplier indicates stronger price performance."
          >
            <Info /> Current / ATH
          </div>
          <div className="dex-item">
            {athData !== undefined && (
              <>
                {formatH(athData.basePrice, athData.listingPrice)} / {formatH(athData.athPrice, athData.listingPrice)}
              </>
            )}
          </div>
        </div>
        <div className='dex-info'>
          <div 
            className="dex-title" 
            data-tooltip-id="global-tooltip"
            data-tooltip-content="Holders: The number of unique wallet addresses that hold this token."
          >
            <Info /> Holders
          </div>
          <div className="dex-item">
            {holderData?.totalHolders !== undefined && holderData?.totalHolders !== null && holderData?.totalHolders !== 0
              ? holderData.totalHolders.toLocaleString('en-US')
              : '-'}
          </div>
        </div>
      </div>
      <div className='dex-info-wrapper'>
        <div className='dex-info'>
          <div 
            className="dex-title" 
            data-tooltip-id="global-tooltip"
            data-tooltip-content="Ownership: Shows the current owner of the token contract. 'Renounced' means no centralized owner."
          >
            <Info /> 
            {scannerD && chainId === 501424 ? (
              <>Authorities</>
            ) : <>Ownership</>}
          </div>
          <div className="dex-item">
            {scannerD && chainId !== 501424 && (
              scannerD.owner_address === "0x0000000000000000000000000000000000000000" || scannerD.owner_address === "" || !scannerD.owner_address || scannerD.owner_address === "0x000000000000000000000000000000000000dead"
                ? <>Renounced</>
                : <AddressDisplay address={scannerD.owner_address} />
            )}
            {scannerD && chainId === 501424 && (
              scannerD.freezable.status === '0' && scannerD.mintable.status === '0' && scannerD.transfer_fee_upgradable.status === '0'
                ? <>Revoked</>
                : <>Active</>
            )}
          </div>
        </div>
        <div className='dex-info'>
          <div 
            className="dex-title" 
            data-tooltip-id="global-tooltip"
            data-tooltip-content="Pair age: The duration for which the trading pair has been active."
          >
            <Info /> Pair age
          </div>
          <div className="dex-item">
            {data && (
              <>{relativeTime(data.pairCreatedTime)}</>
            )}
          </div>
        </div>
      </div>
      <div className='dex-info-wrapper'>
        <div className='dex-info'>
          <div 
            className="dex-title" 
            data-tooltip-id="global-tooltip"
            data-tooltip-content="Buy fee: The fee charged when purchasing the token."
          >
            <Info /> Buy fee
          </div>
          <div className="dex-item">
            {scannerD && chainId && chainId === 501424 ? (
              <>{(scannerD.transfer_fee?.current_fee_rate?.fee_rate ?? 0)}%</>
            ) : <>{((scannerD?.buy_tax && scannerD.buy_tax.toString().trim() !== "") ? scannerD.buy_tax * 100 : 0)}%</>} 
          </div>
        </div>
        <div className='dex-info'>
          <div 
            className="dex-title" 
            data-tooltip-id="global-tooltip"
            data-tooltip-content="Sell fee: The fee charged when selling the token."
          >
            <Info /> Sell fee
          </div>
          <div className="dex-item">
            {scannerD && chainId && chainId === 501424 ? (
              <>{(scannerD.transfer_fee?.current_fee_rate?.fee_rate ?? 0)}%</>
            ) : <>{((scannerD?.sell_tax && scannerD.sell_tax.toString().trim() !== "") ? scannerD.sell_tax * 100 : 0)}%</>} 
          </div>
        </div>
      </div>
      <Tooltip id="global-tooltip" className='my-tooltip'/>
    </>
  );
};

export default DexInfo;
